import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, MapPin, Share2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import LuxuryQuotePreviewCard from "@/components/LuxuryQuotePreviewCard";
import { format } from "date-fns";

interface WeatherData {
  temperature: number;
  feelsLike: number;
  description: string;
  humidity: number;
  wind: string;
}

interface AIInsights {
  pricingAnalysis?: {
    explanation: string;
  };
  tripInsights?: {
    routeNotes: string;
    trafficEstimate?: string;
  };
  tips?: string[];
}
interface QuoteResult {
  distance: number;
  time: string;
  subtotal: number;
  gratuity: number;
  total: number;
  gratuityIncluded?: boolean;
  isSATPickup?: boolean;
  isSATPickupExtended?: boolean;
  isFixedRoute?: boolean;
  isFlat?: boolean;
  note?: string;
  breakdown: {
    baseFee?: number;
    baseRate?: number;
    mileageCharge?: number;
    timeCharge?: number;
    extraMiles?: number;
    extraMileCharge?: number;
    extraMinutes?: number;
    extraTimeCharge?: number;
    extraStops?: number;
    lateNightFee: number;
    lastMinuteFee?: number;
    gratuity?: number;
    gratuityIncluded?: boolean;
    minimumApplied?: boolean;
    satMinimumFare?: number;
  };
}

const QuickQuoteCalculator = () => {
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropoffAddress, setDropoffAddress] = useState("");
  const [pickupDate, setPickupDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [pickupTime, setPickupTime] = useState("12:00");
  const [loading, setLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState<QuoteResult | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [aiInsights, setAIInsights] = useState<AIInsights | null>(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualDistance, setManualDistance] = useState("");
  const [manualDuration, setManualDuration] = useState("");
  // Last-Minute Fee is now auto-applied by the backend when pickup is within 2 hours.
  const isLastMinute = useCallback(() => {
    if (!pickupDate || !pickupTime) return false;
    const [h, m] = pickupTime.split(":");
    const pickup = new Date(`${pickupDate}T${(h || "12").padStart(2, "0")}:${(m || "00").padStart(2, "0")}:00`);
    const diffMs = pickup.getTime() - Date.now();
    return diffMs <= 2 * 60 * 60 * 1000 && diffMs > -15 * 60 * 1000;
  }, [pickupDate, pickupTime]);

  // Fetch weather data for pickup location
  const fetchWeather = useCallback(async () => {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=29.4241&longitude=-98.4936&current=temperature_2m,apparent_temperature,weather_code,relative_humidity_2m,wind_speed_10m,wind_direction_10m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FChicago`;
      const res = await fetch(url);
      const data = await res.json();
      
      const current = data.current;
      const windDir = getWindDirection(current.wind_direction_10m);
      
      setWeather({
        temperature: Math.round(current.temperature_2m),
        feelsLike: Math.round(current.apparent_temperature),
        description: getWeatherDescription(current.weather_code),
        humidity: current.relative_humidity_2m,
        wind: `${Math.round(current.wind_speed_10m)} mph ${windDir}`
      });
    } catch (err) {
      console.error('Weather fetch error:', err);
    }
  }, []);

  const getWindDirection = (degrees: number): string => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: 'Clear', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
      45: 'Foggy', 48: 'Rime Fog', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
      61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain', 71: 'Light Snow', 73: 'Snow',
      75: 'Heavy Snow', 80: 'Rain Showers', 81: 'Rain Showers', 82: 'Heavy Showers',
      95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Severe Thunderstorm'
    };
    return descriptions[code] || 'Unknown';
  };

  // Generate AI insights based on trip data
  const generateAIInsights = useCallback((distance: number, duration: number, pricingMethod: string) => {
    const isLateNight = pickupTime && (parseInt(pickupTime.split(':')[0]) >= 21 || parseInt(pickupTime.split(':')[0]) < 5);
    
    const insights: AIInsights = {
      pricingAnalysis: {
        explanation: pricingMethod === 'flat-rate'
          ? 'Flat rate destination - fixed pricing applies.'
          : distance < 15
            ? 'Local service rate applied for short-distance trip.'
            : `Distance exceeds 15-mile local threshold, using formula pricing.`
      },
      tripInsights: {
        routeNotes: distance > 30 
          ? 'Via major highway for fastest route'
          : 'Using optimal local routes',
        trafficEstimate: isLateNight 
          ? 'Light traffic expected at this time'
          : 'Normal traffic conditions expected'
      },
      tips: []
    };

    if (pickupAddress.toLowerCase().includes('airport') || pickupAddress.toLowerCase().includes('sat')) {
      insights.tips?.push('Allow 10-15 minutes for airport pickup procedures');
      insights.tips?.push('Confirm your flight arrival time');
    }
    
    if (isLateNight) {
      insights.tips?.push('Late-Night/Early Morning Surcharge applied (9 PM - 5 AM)');
    }

    if (distance > 50) {
      insights.tips?.push('Consider a comfort break for longer journey');
    }

    setAIInsights(insights);
  }, [pickupTime, pickupAddress]);

  const calculatePrice = async () => {
    if (!pickupAddress || !dropoffAddress || !pickupDate || !pickupTime) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      // Check for Austin Airport flat rate override (gratuity NOT included in base)
      const isAustinAirport = dropoffAddress.toLowerCase().includes("austin") &&
                             (dropoffAddress.toLowerCase().includes("airport") ||
                              dropoffAddress.toLowerCase().includes("bergstrom") ||
                              dropoffAddress.toLowerCase().includes("aus"));

      if (isAustinAirport) {
        const baseFare = 395;
        const isLN = pickupTime && (parseInt(pickupTime.split(':')[0]) >= 21 || parseInt(pickupTime.split(':')[0]) < 5);
        const lnFee = isLN ? 35 : 0;
        const lmFee = isLastMinute() ? 25 : 0;
        const subtotal = baseFare + lnFee + lmFee;
        const grat = subtotal * 0.20;
        setQuoteResult({
          distance: 80,
          time: "1 hour 15 minutes",
          subtotal: subtotal,
          gratuity: grat,
          total: subtotal + grat,
          gratuityIncluded: false,
          isFixedRoute: true,
          isFlat: true,
          breakdown: {
            baseFee: baseFare,
            mileageCharge: 0,
            lateNightFee: lnFee,
            lastMinuteFee: lmFee,
          },
          note: "Austin airport flat rate. 20% gratuity applied to base fare and surcharges."
        });
        setLoading(false);
        return;
      }

      // Check for SAT Airport Pickup (pickup from SAT, < 15 miles)
      const isSATPickup = pickupAddress.toLowerCase().match(/\b(sat|san antonio airport|sat airport|sa airport|satx)\b/);
      if (isSATPickup) {
        // SAT Pickup - let the edge function handle it, but we can detect it for the local Austin override above
        // Fall through to edge function which has the full SAT pickup logic
      }

      // Check for manual distance/duration overrides
      if (manualDistance && manualDuration) {
        const distance = parseFloat(manualDistance);
        const duration = parseFloat(manualDuration);
        
        if (!isNaN(distance) && !isNaN(duration)) {
          // Use manual values with edge function, passing them as overrides
          const { data, error } = await supabase.functions.invoke('calculate-quote', {
            body: {
              pickupAddress,
              dropoffAddress,
              pickupDate,
              pickupTime,
              manualDistance: distance,
              manualDuration: duration,
            }
          });

          if (error) throw error;
          setQuoteResult(data);
          fetchWeather();
          const dur = typeof data.time === 'string' ? parseFloat(data.time) : data.time;
          generateAIInsights(data.distance, dur, data.isFlat ? 'flat-rate' : 'formula');
          setLoading(false);
          return;
        }
      }

      // Call edge function to calculate distance via Google Maps
      const { data, error } = await supabase.functions.invoke('calculate-quote', {
        body: {
          pickupAddress,
          dropoffAddress,
          pickupDate,
          pickupTime,
        }
      });

      if (error) throw error;

      setQuoteResult(data);
      
      // Fetch weather and generate AI insights
      fetchWeather();
      const duration = typeof data.time === 'string' ? parseFloat(data.time) : data.time;
      generateAIInsights(data.distance, duration, data.isFlat ? 'flat-rate' : 'formula');

    } catch (error) {
      console.error("Error calculating quote:", error);
      toast.error("Unable to calculate quote. Please check the addresses and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto bg-gradient-to-br from-black via-gray-900 to-black border-2 border-[#D4AF37]/30 shadow-2xl">
      <CardHeader className="space-y-2 text-center">
        <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] bg-clip-text text-transparent">
          Quick Quote Calculator
        </CardTitle>
        <CardDescription className="text-gray-300 text-base">
          Get an instant estimate for your luxury car service
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-4">
          {/* Pickup Address */}
          <div className="space-y-2">
            <Label htmlFor="pickup" className="text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              Pickup Address
            </Label>
            <Input
              id="pickup"
              placeholder="Enter pickup location"
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-400 focus:border-[#D4AF37]"
            />
          </div>

          {/* Drop-off Address */}
          <div className="space-y-2">
            <Label htmlFor="dropoff" className="text-white flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              Drop-off Address
            </Label>
            <Input
              id="dropoff"
              placeholder="Enter destination"
              value={dropoffAddress}
              onChange={(e) => setDropoffAddress(e.target.value)}
              className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-400 focus:border-[#D4AF37]"
            />
            <p className="text-xs text-gray-400">
              SAT Airport pickups: $150 flat (gratuity included) • SAT drop-offs: $132 local rate
            </p>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date" className="text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#D4AF37]" />
                Pickup Date
              </Label>
              <Input
                id="date"
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                className="bg-gray-800 border-[#D4AF37]/30 text-white focus:border-[#D4AF37]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="time" className="text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37]" />
                Pickup Time
              </Label>
              <Input
                id="time"
                type="time"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
                className="bg-gray-800 border-[#D4AF37]/30 text-white focus:border-[#D4AF37]"
              />
              <p className="text-xs text-gray-400">
                Late night pickups (9 PM - 5 AM) include a $35 surcharge
              </p>
            </div>
          </div>

          {/* Last-Minute Fee notice — auto-applied within 2 hours of pickup */}
          <p className="text-xs text-gray-400 italic">
            A $25 Last-Minute Booking fee is automatically applied when pickup is within 2 hours of the request.
          </p>

          {/* Optional Manual Distance/Duration Override */}
          <div className="mt-4">
            <button
              onClick={() => setShowManualInput(!showManualInput)}
              className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              type="button"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${showManualInput ? 'rotate-180' : ''}`} />
              Optional: Enter distance/duration manually
            </button>

            {showManualInput && (
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">Distance (miles)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 25"
                    value={manualDistance}
                    onChange={(e) => setManualDistance(e.target.value)}
                    className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-400">Duration (minutes)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 30"
                    value={manualDuration}
                    onChange={(e) => setManualDuration(e.target.value)}
                    className="bg-gray-800 border-[#D4AF37]/30 text-white placeholder:text-gray-500"
                  />
                </div>
                <p className="col-span-2 text-xs text-gray-500">
                  Leave blank to use Google Maps. Manual values override API.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Get Quote Button */}
        <Button
          onClick={calculatePrice}
          disabled={loading}
          className="w-full bg-gradient-to-r from-[#D4AF37] via-[#FFD700] to-[#D4AF37] text-black font-bold text-lg py-6 hover:shadow-2xl hover:shadow-[#D4AF37]/50 transition-all duration-300"
        >
          {loading ? "Calculating..." : "Get Quote"}
        </Button>

        {/* Quote Result */}
        {quoteResult && (
          <div className="mt-6 p-6 bg-gray-800/50 rounded-lg border-2 border-[#D4AF37]/30 space-y-4 animate-fade-in">
            {/* Investment Summary Header */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-[#D4AF37]">Price Summary</h3>
            </div>

            <div className="grid grid-cols-2 gap-4 text-white">
              <div>
                <p className="text-sm text-gray-400">Estimated Distance</p>
                <p className="text-lg font-semibold">{quoteResult.distance} miles</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Estimated Time</p>
                <p className="text-lg font-semibold">{quoteResult.time}</p>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2">
              {/* Base/Flat Rate */}
              <div className="flex justify-between text-gray-300 text-xs">
                <span>
                  {quoteResult.isSATPickupExtended
                    ? 'SAT Pickup Base (Gratuity Incl.)'
                    : quoteResult.breakdown?.mileageCharge ? 'Base Service' : 'Flat Rate Service'}
                </span>
                <span>${(quoteResult.breakdown?.baseFee ?? quoteResult.breakdown?.baseRate ?? quoteResult.subtotal)?.toFixed(2)}</span>
              </div>

              {/* SAT Extended: Extra Miles beyond 15 */}
              {quoteResult.isSATPickupExtended && (quoteResult.breakdown?.extraMileCharge ?? 0) > 0 && (
                <div className="flex justify-between text-gray-300 text-xs">
                  <span>Extra Miles ({quoteResult.breakdown.extraMiles?.toFixed(1)} mi × $3.25)</span>
                  <span>${quoteResult.breakdown.extraMileCharge?.toFixed(2)}</span>
                </div>
              )}

              {/* SAT Extended: Extra Time beyond 18 min */}
              {quoteResult.isSATPickupExtended && (quoteResult.breakdown?.extraTimeCharge ?? 0) > 0 && (
                <div className="flex justify-between text-gray-300 text-xs">
                  <span>Extra Time ({Math.round(quoteResult.breakdown.extraMinutes ?? 0)} min × $1.25)</span>
                  <span>${quoteResult.breakdown.extraTimeCharge?.toFixed(2)}</span>
                </div>
              )}

              {/* Mileage - only for formula pricing */}
              {!quoteResult.isSATPickupExtended && (quoteResult.breakdown?.mileageCharge ?? 0) > 0 && (
                <div className="flex justify-between text-gray-300 text-xs">
                  <span>Mileage ({quoteResult.distance} mi × $3.25)</span>
                  <span>${quoteResult.breakdown.mileageCharge?.toFixed(2)}</span>
                </div>
              )}

              {/* Time - only for formula pricing */}
              {!quoteResult.isSATPickupExtended && (quoteResult.breakdown?.timeCharge ?? 0) > 0 && (
                <div className="flex justify-between text-gray-300 text-xs">
                  <span>Time ({typeof quoteResult.time === 'string' ? parseFloat(quoteResult.time) : quoteResult.time} min × $1.25)</span>
                  <span>${quoteResult.breakdown.timeCharge?.toFixed(2)}</span>
                </div>
              )}

              {/* Additional Stops - show when stopCost > 0 (ALL service types) */}
              {(quoteResult.breakdown?.extraStops ?? 0) > 0 && (
                <div className="flex justify-between text-gray-300 text-xs">
                  <span>Additional Stops (× $25.00)</span>
                  <span>${quoteResult.breakdown.extraStops?.toFixed(2)}</span>
                </div>
              )}

              {/* Late-Night Surcharge */}
              {(quoteResult.breakdown?.lateNightFee ?? 0) > 0 && (
                <div className="flex justify-between text-yellow-500 text-xs font-bold">
                  <span>Late-Night/Early Morning Surcharge (9 PM - 5 AM)</span>
                  <span>${quoteResult.breakdown.lateNightFee.toFixed(2)}</span>
                </div>
              )}

              {/* Last Minute Fee */}
              {(quoteResult.breakdown?.lastMinuteFee ?? 0) > 0 && (
                <div className="flex justify-between text-orange-400 text-xs font-bold">
                  <span>Last Minute Booking Fee</span>
                  <span>${quoteResult.breakdown.lastMinuteFee!.toFixed(2)}</span>
                </div>
              )}

              {/* SAT Airport Minimum */}
              {quoteResult.breakdown?.minimumApplied && (
                <div className="flex justify-between text-emerald-400 text-xs">
                  <span>✓ SAT Airport Minimum Applied</span>
                  <span>${quoteResult.breakdown.satMinimumFare?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Subtotal & Gratuity */}
            <div className="border-t border-zinc-800 pt-2 space-y-1">
              <div className="flex justify-between text-gray-300">
                <span>Subtotal</span>
                <span>${quoteResult.subtotal?.toFixed(2)}</span>
              </div>
              
              {quoteResult.gratuityIncluded ? (
                <div className="flex justify-between text-green-400 font-semibold">
                  <span>Gratuity (20%) ✓</span>
                  <span>Included</span>
                </div>
              ) : (
                <div className="flex justify-between text-gray-300">
                  <span>Gratuity (20%)</span>
                  <span>${quoteResult.gratuity?.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Total */}
            <div className="bg-yellow-900/20 p-3 rounded-lg border border-yellow-600/40 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-yellow-600 font-bold uppercase">Total</span>
                <span className="text-yellow-500 text-2xl font-bold">${quoteResult.total.toFixed(2)}</span>
              </div>
              {quoteResult.gratuityIncluded && (
                <div className="text-center mt-2">
                  <span className="bg-green-500/20 border border-green-500 text-green-400 text-xs px-3 py-1 rounded-full">
                    ✓ 20% Gratuity Included
                  </span>
                </div>
              )}
            </div>

            {/* SAT Pickup info box */}
            {quoteResult.isSATPickup && !quoteResult.isSATPickupExtended && (
              <div className="mt-2 p-2 bg-emerald-900/20 rounded text-xs text-emerald-300 border border-emerald-800/30">
                <p className="font-semibold mb-1">Includes:</p>
                <p>1 hour wait time • Flight monitoring • 15-mile drop-off radius</p>
              </div>
            )}

            {quoteResult.isSATPickupExtended && (
              <div className="mt-2 p-2 bg-emerald-900/20 rounded text-xs text-emerald-300 border border-emerald-800/30">
                <p className="font-semibold mb-1">✓ Gratuity & 1 Hr Wait Time included</p>
                <p>SAT pickup base covers first 15 mi & 18 min — extras billed at standard rates.</p>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center pt-2">
              {quoteResult.gratuityIncluded
                ? "*Gratuity included in base rate."
                : quoteResult.note || "*Final price may vary based on actual route and traffic conditions"}
            </p>

            {/* Share Quote Button */}
            <Button
              onClick={() => setShowShareDialog(true)}
              variant="outline"
              className="w-full mt-4 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Quote
            </Button>
          </div>
        )}
      </CardContent>

      {/* Share Quote Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md max-h-[90vh] overflow-y-auto">
          <LuxuryQuotePreviewCard
            quote={{
              pickupAddress: pickupAddress || 'San Antonio, TX',
              dropoffAddress: dropoffAddress,
              pickupDate: pickupDate || format(new Date(), 'yyyy-MM-dd'),
              pickupTime: pickupTime || '12:00',
              distance: quoteResult?.distance ?? 0,
              duration: typeof quoteResult?.time === 'string' 
                ? parseFloat(quoteResult.time) 
                : (quoteResult?.time ?? 0),
              total: quoteResult?.total ?? 0,
              pricingMethod: quoteResult?.isFlat 
                ? (quoteResult?.gratuityIncluded ? 'flat-rate' : 'formula') 
                : 'formula',
              gratuityIncluded: quoteResult?.gratuityIncluded,
              flatRateDestination: undefined,
              baseFare: quoteResult?.isFlat && quoteResult?.gratuityIncluded
                ? (quoteResult?.breakdown?.baseRate ?? quoteResult?.subtotal)
                : (quoteResult?.breakdown?.baseFee ?? quoteResult?.breakdown?.baseRate),
              mileageCost: quoteResult?.breakdown?.mileageCharge,
              timeCost: quoteResult?.breakdown?.timeCharge,
              additionalStopsCost: quoteResult?.breakdown?.extraStops,
              lateNightSurcharge: quoteResult?.breakdown?.lateNightFee,
              lastMinuteFee: quoteResult?.breakdown?.lastMinuteFee,
              gratuity: quoteResult?.gratuity,
            }}
            weather={weather}
            aiInsights={aiInsights}
            onClose={() => setShowShareDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default QuickQuoteCalculator;
